import { tmdb } from './tmdb';
import { WatchlistItem } from './store';
import { UpdateScheduler } from './updateScheduler';

export interface ContentUpdate {
  showId: number;
  hasNewSeason: boolean;
  newSeasonNumber?: number;
  hasNewEpisodes: boolean;
  nextEpisode?: {
    name: string;
    season: number;
    episode: number;
    airDate: string;
  };
  status?: string;
  nextCheckDate?: string;
  updateFrequency?: number;
  totalSeasons?: number;
}

export class ContentChecker {
  /**
   * Check for updates on shows that are due for checking
   * Uses intelligent scheduling based on show status
   */
  static async checkForUpdates(watchlistItems: WatchlistItem[], forceCheck = false): Promise<ContentUpdate[]> {
    const updates: ContentUpdate[] = [];

    const tvShows = watchlistItems.filter(item => item.type === 'tv');

    // Filter to only shows that are due for update (unless force check)
    const showsToCheck = forceCheck
      ? tvShows
      : tvShows.filter(show => UpdateScheduler.isDueForUpdate(show));

    for (const show of showsToCheck) {
      try {
        const contentUpdate = await this.checkShowForUpdates(show);
        updates.push(contentUpdate);
      } catch (error) {
        // Error checking show
      }

      // Add delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 250));
    }

    return updates;
  }

  /**
   * Whether the user has watched a specific episode (via a fully watched
   * season or an individual episode mark)
   */
  private static hasWatchedEpisode(item: WatchlistItem, season: number, episode: number): boolean {
    if (item.watchedSeasons?.includes(season)) return true;
    return !!item.watchedEpisodes?.[season]?.includes(episode);
  }

  /**
   * Check a single show for updates and calculate next update schedule
   */
  private static async checkShowForUpdates(watchlistItem: WatchlistItem): Promise<ContentUpdate> {
    const showDetails = await tmdb.getShow(watchlistItem.id);

    // Calculate update schedule based on show status and timing
    const schedule = UpdateScheduler.calculateUpdateSchedule(showDetails);

    const hasNewSeason = (showDetails.number_of_seasons || 0) > (watchlistItem.totalSeasons || 0);

    // "New episodes" means aired content the user hasn't watched yet.
    // TMDB's next_episode_to_air alone flags every airing show as having
    // new content, even for users who are fully caught up.
    const lastAired = showDetails.last_episode_to_air;
    const hasNewEpisodes = !!lastAired &&
      !this.hasWatchedEpisode(watchlistItem, lastAired.season_number, lastAired.episode_number);

    const update: ContentUpdate = {
      showId: watchlistItem.id,
      hasNewSeason,
      hasNewEpisodes,
      status: UpdateScheduler.normalizeStatus(showDetails.status),
      nextCheckDate: schedule.nextCheckDate,
      updateFrequency: schedule.updateFrequency,
      totalSeasons: showDetails.number_of_seasons,
    };

    if (hasNewSeason) {
      update.newSeasonNumber = showDetails.number_of_seasons;
    }

    // Upcoming-episode pointer is independent of the new-content flag — the
    // card uses it to show "Airs <date>" even for caught-up shows
    if (showDetails.next_episode_to_air) {
      update.nextEpisode = {
        name: showDetails.next_episode_to_air.name,
        season: showDetails.next_episode_to_air.season_number,
        episode: showDetails.next_episode_to_air.episode_number,
        airDate: showDetails.next_episode_to_air.air_date,
      };
    }

    return update;
  }

  /**
   * Check a single show immediately (used when adding to watchlist)
   * Always returns full metadata including schedule
   */
  static async checkSingleShow(showId: number, currentTotalSeasons: number = 0): Promise<ContentUpdate> {
    try {
      const showDetails = await tmdb.getShow(showId);

      // Calculate update schedule
      const schedule = UpdateScheduler.calculateUpdateSchedule(showDetails);

      const hasNewSeason = (showDetails.number_of_seasons || 0) > currentTotalSeasons;
      const hasNewEpisodes = !!showDetails.next_episode_to_air;

      const update: ContentUpdate = {
        showId,
        hasNewSeason,
        hasNewEpisodes,
        status: UpdateScheduler.normalizeStatus(showDetails.status),
        nextCheckDate: schedule.nextCheckDate,
        updateFrequency: schedule.updateFrequency,
        totalSeasons: showDetails.number_of_seasons,
      };

      if (hasNewSeason) {
        update.newSeasonNumber = showDetails.number_of_seasons;
      }

      if (hasNewEpisodes && showDetails.next_episode_to_air) {
        update.nextEpisode = {
          name: showDetails.next_episode_to_air.name,
          season: showDetails.next_episode_to_air.season_number,
          episode: showDetails.next_episode_to_air.episode_number,
          airDate: showDetails.next_episode_to_air.air_date,
        };
      }

      return update;
    } catch (error) {
      return {
        showId,
        hasNewSeason: false,
        hasNewEpisodes: false,
        totalSeasons: currentTotalSeasons,
      };
    }
  }

  /**
   * Legacy method - kept for backward compatibility
   * Use UpdateScheduler.isDueForUpdate() instead
   */
  static shouldCheckForUpdates(lastChecked: string): boolean {
    const lastCheckedDate = new Date(lastChecked);
    const now = new Date();
    const diffInHours = (now.getTime() - lastCheckedDate.getTime()) / (1000 * 60 * 60);

    // Check for updates every 24 hours (fallback)
    return diffInHours >= 24;
  }
}

// Hook for periodic checking in the dashboard
export function useContentChecker() {
  const checkForUpdates = async (watchlist: WatchlistItem[], updateCallback: (updates: ContentUpdate[]) => void) => {
    try {
      const itemsToCheck = watchlist.filter(item =>
        item.type === 'tv' && ContentChecker.shouldCheckForUpdates(item.lastChecked)
      );

      if (itemsToCheck.length === 0) return;

      const updates = await ContentChecker.checkForUpdates(itemsToCheck);
      if (updates.length > 0) {
        updateCallback(updates);
      }
    } catch (error) {
      // Error in periodic check
    }
  };

  return { checkForUpdates };
}